import { Badge, Select } from '@chopchop-se/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PalletRow } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

const STATUS_TONE = {
  active: 'success',
  shipped: 'neutral',
  consumed: 'neutral',
  empty: 'warning',
} as const;

/** All pallets, by LPN or supplier SSCC — both resolve to the same row. */
export function PalletsPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'updatedAt', defaultDir: 'desc' });

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.pallets(listQueryKey(state)),
    queryFn: () => warehouseApi.listPallets(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['pallets', 'stock'], warehouseKeys.pallets());

  const columns: DataTableColumn<PalletRow>[] = [
    {
      id: 'lpn',
      header: t('warehouseCore:pallets.lpn'),
      sortField: 'lpn',
      cell: (row) => <span className="font-mono text-sm text-ink">{row.lpn}</span>,
    },
    {
      id: 'sscc',
      header: t('warehouseCore:pallets.sscc'),
      cell: (row) => (row.ssccAlias ? <span className="font-mono text-xs">{row.ssccAlias}</span> : '—'),
      hideBelow: 'lg',
    },
    {
      id: 'status',
      header: t('warehouseCore:pallets.status'),
      sortField: 'status',
      cell: (row) => (
        <Badge tone={STATUS_TONE[row.status]}>{t(`warehouseCore:pallets.statuses.${row.status}`)}</Badge>
      ),
    },
    {
      id: 'bin',
      header: t('warehouseCore:pallets.bin'),
      sortField: 'binCode',
      cell: (row) => <span className="font-mono text-sm">{row.currentBinCode ?? '—'}</span>,
    },
    {
      id: 'articles',
      header: t('warehouseCore:pallets.articles'),
      align: 'right',
      cell: (row) => row.articleCount,
      hideBelow: 'md',
    },
    {
      id: 'qty',
      header: t('warehouseCore:pallets.quantity'),
      align: 'right',
      cell: (row) => <span className="font-mono">{formatQty(row.totalQtyBase)}</span>,
    },
    {
      id: 'updated',
      header: t('warehouseCore:pallets.updated'),
      sortField: 'updatedAt',
      cell: (row) => formatDateTime(row.updatedAt),
      hideBelow: 'xl',
    },
  ];

  return (
    <>
      <PageHeader title={t('warehouseCore:pallets.title')} subtitle={t('warehouseCore:pallets.subtitle')} />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isFetching}
        error={error}
        searchPlaceholder={t('warehouseCore:pallets.search')}
        emptyTitle={t('warehouseCore:pallets.empty')}
        emptyDescription={t('warehouseCore:pallets.emptyHelp')}
        onRowClick={(row) => {
          void navigate(`/warehouse/pallets/${row.id}`);
        }}
        filters={
          <>
            <Select
              aria-label={t('warehouseCore:pallets.status')}
              className="w-44"
              value={String(state.filters?.status ?? '')}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                ...(['active', 'empty', 'shipped', 'consumed'] as const).map((status) => ({
                  value: status,
                  label: t(`warehouseCore:pallets.statuses.${status}`),
                })),
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('warehouseCore:pallets.hasSscc')}
              className="w-40"
              value={state.filters?.hasSscc === true ? 'yes' : ''}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                { value: 'yes', label: t('warehouseCore:pallets.hasSscc') },
              ]}
              onChange={(event) => {
                setFilter('hasSscc', event.target.value === 'yes' ? true : undefined);
              }}
            />
          </>
        }
      />
    </>
  );
}
